import { vendorFiscalFxReviewSchema, vendorFiscalFxContextSchema, vendorFiscalFxProofSchema, } from './fiscal-fx-vendor.js';
import { z } from 'zod';
import { apSelfAssessmentReviewSchema, apSelfAssessmentContextSchema, } from './ap-self-assessment.js';
import { currencyCodeSchema, idSchema, isoDateSchema, moneySchema, percentSchema, } from './common.js';
import { ledgerExchangeRateSchema } from './ledger-payments.js';
import { vendorInvoiceOriginalEvidenceSchema } from './vendor-invoice-original.js';
import { journalLineInputSchema, ledgerAccountSchema, ledgerDateSchema, ledgerDimensionSchema, } from './financial-accounting.js';
export const vendorInvoiceAccountingAmountSchema = z.object({
    position: z.number().int().min(1).max(9999),
    netAmount: moneySchema,
    taxRate: percentSchema,
    inputTaxDeductionPercent: percentSchema.default('100'),
});
export const vendorInvoiceAccountingSplitWriteSchema = vendorInvoiceAccountingAmountSchema.extend({
    accountId: idSchema,
    costCenterId: idSchema.nullish(),
    costObjectId: idSchema.nullish(),
    description: z.string().trim().max(240).default(''),
});
export const vendorInvoiceAccountingSplitSaveSchema = z
    .object({
    version: z.number().int().positive(),
    selfAssessment: apSelfAssessmentReviewSchema.nullish(),
    fiscalFx: vendorFiscalFxReviewSchema.nullish(),
    splits: z.array(vendorInvoiceAccountingSplitWriteSchema).min(1).max(200),
})
    .superRefine((value, context) => {
    if (new Set(value.splits.map((row) => row.position)).size !== value.splits.length)
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['splits'],
            message: 'ledger.vendorAllocation.duplicatePosition',
        });
});
export const vendorInvoiceAccountingSourceBucketSchema = z.object({
    taxRate: percentSchema,
    netAmount: moneySchema,
    taxAmount: moneySchema,
});
export const vendorInvoiceAccountingCalculationInputSchema = z.object({
    buckets: z.array(vendorInvoiceAccountingSourceBucketSchema).min(1).max(200),
    splits: z.array(vendorInvoiceAccountingAmountSchema).min(1).max(200),
    exchangeRate: ledgerExchangeRateSchema,
});
export const vendorInvoiceAccountingFindingSchema = z.enum([
    'invalidInput',
    'duplicatePosition',
    'allocationTotalMismatch',
    'allocationTaxRateMismatch',
    'allocationSignMismatch',
    'zeroTaxBase',
    'sourceTaxMismatch',
    'amountOverflow',
]);
export const vendorInvoiceAccountingCalculatedRowSchema = vendorInvoiceAccountingAmountSchema.extend({
    sourceTaxAmount: moneySchema,
    deductibleTaxAmount: moneySchema,
    costAmount: moneySchema,
    homeCostAmount: moneySchema,
    homeDeductibleTaxAmount: moneySchema,
});
export const vendorInvoiceAccountingCalculationSchema = z.object({
    eligible: z.boolean(),
    findings: z.array(vendorInvoiceAccountingFindingSchema),
    netAmount: moneySchema.nullable(),
    taxAmount: moneySchema.nullable(),
    grossAmount: moneySchema.nullable(),
    deductibleTaxAmount: moneySchema.nullable(),
    homeGrossAmount: moneySchema.nullable(),
    homeCostAmount: moneySchema.nullable(),
    homeDeductibleTaxAmount: moneySchema.nullable(),
    rows: z.array(vendorInvoiceAccountingCalculatedRowSchema),
});
export const vendorInvoiceAccountingStoredSplitSchema = vendorInvoiceAccountingSplitWriteSchema.extend({
    id: idSchema,
    revision: z.number().int().positive(),
    sourceTaxAmount: moneySchema,
    deductibleTaxAmount: moneySchema,
});
export const vendorAdvanceReviewSchema = z.object({
    appliedGross: moneySchema,
    appliedNet: moneySchema,
    appliedTax: moneySchema,
    appliedDeductibleTax: moneySchema,
    residualDue: moneySchema,
    declaredPrepaid: moneySchema.nullable(),
    declaredDue: moneySchema.nullable(),
    sources: z.array(z.object({
        number: z.string(),
        date: isoDateSchema,
        paidGross: moneySchema,
        gross: moneySchema,
        net: moneySchema,
        tax: moneySchema,
        deductibleTax: moneySchema,
        buckets: z.array(z.object({
            rate: z.string(),
            gross: moneySchema,
            net: moneySchema,
            tax: moneySchema,
            deductibleTax: moneySchema,
        })),
    })),
});
export const vendorInvoiceAccountingPreviewSchema = z.object({
    foreignCreditSource: z
        .object({
        sourceFiscalReviewId: idSchema.nullable(),
        proof: vendorFiscalFxProofSchema,
        positions: z.array(z.object({
            sourcePosition: z.number().int().positive(),
            rate: percentSchema,
            net: moneySchema,
            tax: moneySchema,
            costAccountId: idSchema,
            inputTaxDeductionPercent: percentSchema,
            costCenterId: idSchema.nullish(),
            costObjectId: idSchema.nullish(),
        })),
    })
        .nullish(),
    selfAssessmentContext: apSelfAssessmentContextSchema.optional(),
    fiscalFxContext: vendorFiscalFxContextSchema.optional(),
    advanceReview: vendorAdvanceReviewSchema.nullable().optional(),
    id: idSchema,
    invoiceNumber: z.string(),
    version: z.number().int().positive(),
    accountingRevision: z.number().int().nonnegative(),
    eligible: z.boolean(),
    booked: z.boolean(),
    readOnly: z.boolean(),
    matchingRequired: z.boolean(),
    journalId: idSchema.nullable(),
    noPostingRequired: z.boolean(),
    businessDate: ledgerDateSchema,
    bookingDate: ledgerDateSchema.nullable(),
    taxDate: ledgerDateSchema.nullable(),
    currency: currencyCodeSchema,
    baseCurrency: currencyCodeSchema,
    netAmount: moneySchema,
    taxAmount: moneySchema,
    grossAmount: moneySchema,
    sourceOrigin: z.enum(['MANUAL', 'ELECTRONIC']),
    original: vendorInvoiceOriginalEvidenceSchema.nullable().default(null),
    sourceBuckets: z.array(vendorInvoiceAccountingSourceBucketSchema),
    exchangeRate: ledgerExchangeRateSchema.nullable(),
    exchangeRateSource: z.string().nullable(),
    issues: z.array(z.object({ severity: z.enum(['ERROR', 'WARNING']), code: z.string() })),
    splits: z.array(vendorInvoiceAccountingStoredSplitSchema),
    effectiveSplits: z.array(vendorInvoiceAccountingSplitWriteSchema),
    lines: z.array(journalLineInputSchema),
    accounts: z.array(ledgerAccountSchema),
    availableAccounts: z.array(ledgerAccountSchema),
    availableDimensions: z.array(ledgerDimensionSchema),
    dimensions: z.array(ledgerDimensionSchema),
    calculation: vendorInvoiceAccountingCalculationSchema.nullable(),
    previewHash: z.string().regex(/^[a-f0-9]{64}$/u),
});
//# sourceMappingURL=vendor-invoice-accounting.js.map